import React, { useCallback, useState, useEffect } from "react"
import type { Editor } from "@tiptap/react"
import { NodeSelection } from "@tiptap/pm/state"

// --- Hooks ---
import { useTiptapEditor } from "../../../hooks/use-tiptap-editor"

// --- Lib ---
import { isNodeTypeSelected } from "../../../lib/tiptap-utils"
import {
  POST_EDITOR_FLOATING_MENU_KINDS,
  postEditorFloatingContainerClasses,
  postEditorFloatingToolbarGroupClasses,
  postEditorFloatingToolbarProps,
} from "../../../../app/javascript/lib/post_editor_constants.js"

// --- Tiptap UI ---
import { DeleteNodeButton } from "../../../components/tiptap-ui/delete-node-button"
import { ImageDownloadButton } from "../../../components/tiptap-ui/image-download-button"
import { ImageCaptionButton } from "../../../components/tiptap-ui/image-caption-button"

// --- UI Primitive ---
import { Separator } from "../../../components/tiptap-ui-primitive/separator"
import { Button } from "../../../components/tiptap-ui-primitive/button"

// --- Icons ---
import { RefreshCcwIcon } from "../../../components/tiptap-icons/refresh-ccw-icon"
import { TagIcon } from "../../tiptap-icons/tag-icon"

interface ImageNodeFloatingProps {
  editor?: Editor | null
  /** BEM kind slug for element-specific floating-menu CSS (default: image). */
  floatingKind?: string
  /** When true, render only the toolbar (parent supplies the floating container). */
  embedded?: boolean
}

export const ImageNodeFloating: React.FC<ImageNodeFloatingProps> = ({
  editor: providedEditor,
  floatingKind = POST_EDITOR_FLOATING_MENU_KINDS.image,
  embedded = false,
}) => {
  const { editor } = useTiptapEditor(providedEditor)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const isImageSelected = isNodeTypeSelected(editor, ["image"])

  // Listen for ResourceModal open/close to hide floating menu
  useEffect(() => {
    const handleModalOpen = () => {
      setIsModalOpen(true)
    }

    const checkModal = () => {
      const modalOverlay = document.querySelector('.resource-modal-overlay')
      const modalIsOpen = !!modalOverlay
      setIsModalOpen(prev => {
        if (prev !== modalIsOpen) {
          return modalIsOpen
        }
        return prev
      })
    }

    document.addEventListener('tiptap:image:requestReplace', handleModalOpen)
    document.addEventListener('tiptap:video:requestReplace', handleModalOpen)
    document.addEventListener('tiptap:downloadRow:requestReplace', handleModalOpen)
    document.addEventListener('tiptap:externalLinkRow:requestReplace', handleModalOpen)

    const interval = setInterval(checkModal, 100)

    const observer = new MutationObserver(checkModal)
    observer.observe(document.body, {
      childList: true,
      subtree: true
    })

    checkModal()

    return () => {
      document.removeEventListener('tiptap:image:requestReplace', handleModalOpen)
      document.removeEventListener('tiptap:video:requestReplace', handleModalOpen)
      document.removeEventListener('tiptap:downloadRow:requestReplace', handleModalOpen)
      document.removeEventListener('tiptap:externalLinkRow:requestReplace', handleModalOpen)
      clearInterval(interval)
      observer.disconnect()
    }
  }, [])

  const visible = isImageSelected && !isModalOpen

  const handleReplace = useCallback(() => {
    if (!editor) return

    try {
      const { selection } = editor.state
      if (!(selection instanceof NodeSelection)) return

      const nodePos = selection.from
      const node = selection.node

      if (!node || node.type.name !== 'image') return

      const nodeDOM = editor.view.nodeDOM(nodePos)

      if (nodeDOM) {
        const ev = new CustomEvent('tiptap:image:requestReplace', {
          bubbles: true,
          cancelable: true,
          detail: {
            nodePos: nodePos,
            resourceId: node.attrs?.resourceId || '',
            src: node.attrs?.src || '',
          }
        })

        nodeDOM.dispatchEvent(ev)
        if (!ev.defaultPrevented) {
          document.dispatchEvent(ev)
        }
      }
    } catch (e) {
      console.error('Failed to dispatch image replacement event:', e)
    }
  }, [editor])

  const openResourceTags = useCallback(() => {
    if (!editor) return
    try {
      const { selection } = editor.state
      if (!(selection instanceof NodeSelection)) return
      const node = selection.node
      if (!node || node.type.name !== "image") return
      const rid = node.attrs?.resourceId
      if (!rid) return
      const ev = new CustomEvent('tiptap:resource:requestTags', { bubbles: true, cancelable: true, detail: { resourceId: String(rid) } })
      document.dispatchEvent(ev)
    } catch (e) {
      // ignore
    }
  }, [editor])

  if (!editor || !visible) return null

  const toolbar = (
    <div {...postEditorFloatingToolbarProps(floatingKind)}>
      <div
        role="group"
        className={postEditorFloatingToolbarGroupClasses(floatingKind)}
      >
        <ImageCaptionButton editor={editor} />
        <Separator orientation="vertical" />
        <ImageDownloadButton editor={editor} />
        <Separator orientation="vertical" />
        <Button
          onClick={openResourceTags}
          tooltip="Tags"
          data-style="ghost"
          data-weight="small"
        >
          <TagIcon className="tiptap-button-icon" />
        </Button>
        <Button
          onClick={handleReplace}
          tooltip="Replace"
          data-style="ghost"
          data-weight="small"
        >
          <RefreshCcwIcon className="tiptap-button-icon" />
        </Button>
        <Separator orientation="vertical" />
        <DeleteNodeButton editor={editor} />
      </div>
    </div>
  )

  if (embedded) return toolbar

  return (
    <div
      tabIndex={-1}
      data-floating-ui-focusable=""
      className={postEditorFloatingContainerClasses(floatingKind)}
      data-floating-kind={floatingKind}
    >
      {toolbar}
    </div>
  )
}
